import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { resolveQuoteProfessionalName } from "@/lib/quoteProfessionalName";
import { QuoteBreakdown } from "@/components/features/notarial-requests/quote-breakdown";
import type { QuoteForConsumer } from "@/services/quoteRequestsService";

/**
 * The two figure columns, as classes rather than numbers, shared by the rows
 * and by the header above them. They are only columns if they are the SAME
 * width in both places, and a header that drifts from its rows is worse than
 * no header — so neither side owns these alone.
 */
const TOTAL_COLUMN = "w-28 shrink-0 text-right";
const ETA_COLUMN = "w-20 shrink-0 text-right";

/**
 * Column labels for a list of bids.
 *
 * WHY THIS EXISTS: the row design is an argument that a column of prices can
 * be read down without moving your eye — which is true from the second bid
 * onward and says nothing at the first. With one offer there is no column to
 * read, and "3 días" is a bare number next to a bare amount. The labels also
 * name the headline figure as the TOTAL, so the same number appearing again
 * at the foot of the expanded breakdown reads as the sum confirming it rather
 * than as a second, different figure.
 *
 * One header for the list, not a label per row: repeating "Entrega estimada:"
 * down ten bids is the noise the column layout exists to avoid.
 */
export function QuoteReviewColumnHeader(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      aria-hidden
      className="flex items-center gap-x-4 px-3 text-xs font-medium text-muted-foreground"
    >
      <span className="min-w-0 flex-1" />
      <span className={TOTAL_COLUMN}>{t("requests.user.quoteReviewColumnTotal")}</span>
      <span className={ETA_COLUMN}>{t("requests.user.quoteReviewColumnEta")}</span>
    </div>
  );
}

export interface QuoteReviewCardProps {
  quote: QuoteForConsumer;
  /** True while the request can still be awarded (status === 'quoting'). */
  canAward: boolean;
  /** Disables the award button while any award mutation is in flight. */
  isAwarding: boolean;
  onAward: (quoteId: string) => void;
  onReject: (quoteId: string) => void;
  /**
   * Whether this request has actually been PAID. An awarded quote is a choice,
   * not a settled job: it lapses back to quoting if the payment window passes,
   * so "Adjudicada" on an unpaid award tells the consumer the matter is closed
   * when it is still waiting on them.
   */
  awardPaid?: boolean;
}

/**
 * One professional's bid, as a ROW rather than a card.
 *
 * It was a ~280px card with the price, the delivery time, every kind
 * subtotal, the IVA note and two buttons all expanded at once. Three bids
 * filled a screen; ten would be unusable, and this list has no ceiling — every
 * professional in a discipline can bid, on each of two legs.
 *
 * Worse, it could not do the one job it exists for. Prices in stacked cards
 * never line up, so comparing them means scrolling and remembering. Airtasker
 * (offers on a task), Fiverr and Oyster all solve this the same way: a compact
 * row per offer with the figures in aligned columns, the accept action on the
 * row, and everything else behind a disclosure.
 *
 * So: name, total and delivery time on one line, with the breakdown and the
 * message revealed on demand. Collapsed by default even when there is only one
 * bid, because a list whose height depends on how many offers arrived is the
 * thing being fixed.
 *
 * The two ACTIONS are not on that line. They sit together at the foot of the
 * card, after the information they act on and outside the disclosure — see the
 * note above the action row for why, and for the surface that settled it first.
 */
export function QuoteReviewCard({
  quote,
  canAward,
  isAwarding,
  onAward,
  onReject,
  awardPaid = false,
}: QuoteReviewCardProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const decided =
    quote.status === "awarded" ? (
      // Chosen and paid are different states, and the consumer is the one who
      // has to act on the difference.
      <StatusBadge variant={awardPaid ? "success" : "warning"}>
        {t(
          awardPaid
            ? "requests.user.quoteReviewAwardedBadge"
            : "requests.user.quoteReviewAwardedPendingPaymentBadge",
        )}
      </StatusBadge>
    ) : quote.status === "rejected" ? (
      <StatusBadge variant="softDanger">
        {t("requests.user.quoteReviewRejectedBadge")}
      </StatusBadge>
    ) : null;

  const displayName = resolveQuoteProfessionalName(quote, t);

  const canChoose = decided == null && canAward;

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
        {/* The disclosure is the name itself, so the whole identity is the
            target rather than a lone chevron in a corner. */}
        {/* The TRACK takes the free space, not the button. When the button was
            itself `flex-1` its focus ring stretched from the name to the price
            column — a long empty rounded rectangle, which is the shape of a
            text input, and it read as one. The ring now hugs the name. */}
        <div className="flex min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            className="flex min-w-0 max-w-full items-center gap-2 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronDown
              className={`size-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
              aria-hidden
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground">
                {displayName}
              </span>
              {/* THE CHEVRON WAS THE ONLY CLUE, and a 16px chevron beside a
                  name does not read as "there is more here" — it reads as
                  decoration. One line says what opening it gives you, and it
                  changes with the state so a row already open does not keep
                  inviting a click that closes it. */}
              <span className="block truncate text-xs text-muted-foreground">
                {t(
                  open
                    ? "requests.user.quoteReviewRowHideDetail"
                    : "requests.user.quoteReviewRowShowDetail",
                )}
              </span>
            </span>
          </button>
        </div>

        {/* Fixed widths, right-aligned, tabular: the point of a row is that
            the column of prices can be read down without moving your eye.

            THE HEADLINE IS totalMinor, NOT priceMinor. This row used to show
            the professional's price, which is not what the consumer pays and
            not what any of the competing bids costs: the charge is that price
            plus delivery, and — until the fee model changes — plus the
            platform fee and the professional's IVA. Comparing three bids on a
            number that is nobody's price, then meeting the real one in the
            award dialog AFTER choosing, is the defect this fixes.

            Rendered straight from the server's own figure, never re-derived
            here. The same shared method computes it and performs the charge
            (decision 3299b7a9), so a total displayed from different arithmetic
            than the one taken is exactly the divergence that rule prevents —
            and it means the fee-model change flows through with no edit here. */}
        <span className={`${TOTAL_COLUMN} text-base font-semibold tabular-nums text-foreground`}>
          {formatUyMinor(quote.totalMinor, quote.currency)}
        </span>
        <span className={`${ETA_COLUMN} text-sm tabular-nums text-muted-foreground`}>
          {quote.estimatedDays != null
            ? t("requests.user.quoteReviewEtaDays", { count: quote.estimatedDays })
            : "—"}
        </span>

        {/* The badge stays on the header row — it is a STATE of this bid, read
            alongside its figures, not an action taken on it. */}
        {decided ? <div className="flex shrink-0 items-center">{decided}</div> : null}
      </div>

      {open ? (
        <div className="space-y-3 border-t border-border bg-muted/20 px-3 py-3">
          <QuoteBreakdown
            lineItems={quote.lineItems}
            currency={quote.currency}
            pricesIncludeIva={quote.pricesIncludeIva}
          />

          {/* What the headline is made of. The itemisation above is the
              PROFESSIONAL'S price broken into honorarios, timbres and gastos;
              these are the amounts on top of it, and without them the total
              looks like it disagrees with the lines above.

              Every row is omitted when zero rather than shown as 0, so a
              digital job with no platform fee reads as a price and a total
              that simply match — which, once the fee model lands, is the
              normal case. */}
          <dl className="space-y-1 border-t border-border pt-3 text-sm">
            {/* ONLY WHEN THERE IS NO ITEMISATION ABOVE. With a breakdown the
                subtotals already sum to this exact figure two rows up, so
                restating it made the consumer read 5.500 twice on their way to
                the total. On a quote submitted before breakdowns existed it is
                the only subtotal there is, and dropping it unconditionally
                would leave the service fee sitting under nothing. */}
            {quote.lineItems == null || quote.lineItems.length === 0 ? (
              <BreakdownRow
                label={t("requests.user.quoteReviewProfessionalPriceIvaIncluded")}
                amountMinor={quote.priceMinor}
                currency={quote.currency}
              />
            ) : null}
            {/* The professional's IVA is NOT a row here any more. It is
                inside priceMinor (spec 76e23408 D6), so adding it as its own
                line would make these rows sum past the total. The price label
                says it is included instead. */}
            <BreakdownRow
              label={t("requests.user.quoteReviewServiceFee")}
              amountMinor={quote.consumerFeeMinor}
              currency={quote.currency}
            />
            <BreakdownRow
              label={t("requests.user.quoteReviewDeliveryFee")}
              amountMinor={quote.deliveryFeeMinor}
              currency={quote.currency}
            />
            <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
              <dt className="font-medium text-foreground">
                {t("requests.user.quoteReviewTotalLabel")}
              </dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {formatUyMinor(quote.totalMinor, quote.currency)}
              </dd>
            </div>
          </dl>

          {/* What this professional needs from you, as a list rather than
              buried in a paragraph — so two bids can be compared on effort
              and not just on price (spec 18c82ff1). No heading when the list
              is empty: every quote predating the field has none, and an empty
              section reads as something missing. */}
          {quote.requiredDocuments?.length ? (
            <div className="space-y-1 rounded-md bg-background px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t("requests.quoteReview.requiredDocumentsTitle")}
              </p>
              <ul className="list-disc space-y-0.5 pl-4">
                {quote.requiredDocuments.map((doc, index) => (
                  <li key={`${doc.label}-${index}`} className="break-words text-sm text-foreground">
                    {doc.label}
                    {doc.kind === "text" ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {t("requests.quoteReview.requiredDocumentsKindText")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

        </div>
      ) : null}

      {/*
        ACTIONS LAST, AND TOGETHER.

        "Elegir" used to sit in the header and "Rechazar" at the foot of the
        disclosure, so the primary action appeared ABOVE the breakdown it was
        asking the consumer to accept, and the two choices about one bid were
        in different places — one of them reachable only once expanded.

        This is not a fresh call. apps/mobile-users' quote-bids-section settled
        it by surveying shipped analogues — inDrive, the closest (competing
        offers for one job), pairs Decline and Accept at the card's foot;
        Airtasker, CVS, Angi and Runna all place a single action last — under
        decision 5bdcbe4, the standing rule for this kind of question. The
        portal was simply the surface still carrying the old arrangement.

        OUTSIDE the disclosure, so choosing does not require expanding first.
        That is what keeping the button in the header bought, and it is worth
        keeping.

        Rechazar stays deliberately quiet: awarding another bid rejects the
        rest by itself, so it is an outline beside a filled button, not a peer.
      */}
      {canChoose ? (
        <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2">
          <Button
            size="sm"
            variant="outline"
            disabled={isAwarding}
            onClick={() => onReject(quote.id)}
          >
            {t("requests.user.quoteReviewRejectButton")}
          </Button>
          <Button size="sm" disabled={isAwarding} onClick={() => onAward(quote.id)}>
            {t("requests.user.quoteReviewAwardButton")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One line of the "what makes up the total" list.
 *
 * Renders NOTHING at zero. A row reading "Costo de envío  $ 0" on a digital
 * job invites the question of why it is there at all, and the fee model
 * landing soon makes two of these four permanently zero — at which point the
 * list should quietly shorten rather than fill with noughts.
 *
 * Baseline-aligned, muted label, tabular amount: the same row grammar as
 * QuoteBreakdown and QuoteAwardSummary, so the three read as one family.
 */
function BreakdownRow({
  label,
  amountMinor,
  currency,
}: {
  label: string;
  amountMinor: number;
  currency: string;
}): JSX.Element | null {
  if (!amountMinor) return null;
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums text-foreground">{formatUyMinor(amountMinor, currency)}</dd>
    </div>
  );
}
